import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SearchOutlined } from '@ant-design/icons';
import { Grid, Input, Modal, Spin, Tag, Typography } from 'antd';
import { useCurrentUser } from '../../../context/UserContext';
import { accounts as httpAccounts } from '../../../pages/Brands/http';
import { filterByAccess, filterStaticActions, STATIC_ACTIONS } from './actions';
import './index.scss';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const DEBOUNCE_MS = 250;

const MATCH_PLATFORM_KEY = (e) => e.key === 'k' && (e.metaKey || e.ctrlKey);

// Global Cmd/Ctrl+K binder. Separate from useKeyboardShortcuts (which ignores
// modifier keys by design — it handles the `?` / `g a` etc. shortcuts).
// `/` is intentionally NOT bound here: ShortcutsProvider already uses it to
// focus the page's own search input, which is usually more useful than
// opening the global palette. Cmd+K fires even while typing — unambiguous.
const useCommandPaletteHotkey = (onToggle) => {
    useEffect(() => {
        const handle = (event) => {
            if (!MATCH_PLATFORM_KEY(event)) return;
            event.preventDefault();
            onToggle();
        };
        window.addEventListener('keydown', handle);
        return () => window.removeEventListener('keydown', handle);
    }, [onToggle]);
};

const LISTBOX_ID = 'command-palette-listbox';
const optionId = (action) => `command-palette-option-${action.id}`;

const ResultRow = ({ action, active, onClick, onHover }) => (
    <div
        id={optionId(action)}
        role='option'
        aria-selected={active}
        tabIndex={-1}
        className={`command-palette__row${active ? ' command-palette__row--active' : ''}`}
        onMouseEnter={onHover}
        onMouseDown={(e) => {
            // Prevent input blur, then run the action
            e.preventDefault();
            onClick();
        }}
    >
        <div className='command-palette__row-main'>
            <Text strong style={{ fontSize: 13 }}>
                {action.label}
            </Text>
            {action.sublabel ? (
                <Text type='secondary' style={{ fontSize: 11, marginLeft: 8 }}>
                    {action.sublabel}
                </Text>
            ) : null}
        </div>
        <Tag aria-hidden='true' style={{ margin: 0, fontSize: 11 }}>
            {action.group}
        </Tag>
    </div>
);

export const CommandPalette = () => {
    const navigate = useNavigate();
    const { accesses } = useCurrentUser();
    const screens = useBreakpoint();
    const isMobile = !screens.sm;

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const [accountResults, setAccountResults] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    const close = useCallback(() => {
        setOpen(false);
        setQuery('');
        setAccountResults([]);
        setActiveIdx(0);
    }, []);

    useCommandPaletteHotkey(() => setOpen((prev) => !prev));

    // Debounced account search — only fires once the query is >= 2 chars.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = query.trim();
        if (q.length < 2 || !open) {
            setAccountResults([]);
            setAccountLoading(false);
            return undefined;
        }
        setAccountLoading(true);
        debounceRef.current = setTimeout(async () => {
            const reqId = ++requestIdRef.current;
            try {
                const res = await httpAccounts.list({
                    limit: 6,
                    search: `store_name:${q},company_name:${q}`
                });
                if (reqId !== requestIdRef.current) return;
                const rows = (res?.data || []).map((a) => ({
                    id: `account-${a.id}`,
                    label: a.storeName || a.companyName || `Account #${a.id}`,
                    sublabel: `#${a.id}${a.companyName && a.companyName !== a.storeName ? ` · ${a.companyName}` : ''}`,
                    group: 'Account',
                    path: `/brands-v2/accounts/${a.id}/users`
                }));
                setAccountResults(rows);
            } catch (_) {
                if (reqId === requestIdRef.current) setAccountResults([]);
            } finally {
                if (reqId === requestIdRef.current) setAccountLoading(false);
            }
        }, DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, open]);

    const allowedStatic = useMemo(() => filterByAccess(STATIC_ACTIONS, accesses), [accesses]);
    const filteredStatic = useMemo(() => filterStaticActions(allowedStatic, query), [allowedStatic, query]);

    const results = useMemo(() => {
        // Account matches float to the top so an admin searching a brand name
        // lands on the dynamic result first.
        return [...accountResults, ...filteredStatic];
    }, [accountResults, filteredStatic]);

    // Reset active index whenever the result shape changes.
    useEffect(() => {
        setActiveIdx(0);
    }, [query, results.length]);

    const execute = useCallback(
        (action) => {
            if (!action) return;
            close();
            navigate(action.path);
        },
        [close, navigate]
    );

    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIdx((idx) => Math.min(idx + 1, Math.max(results.length - 1, 0)));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIdx((idx) => Math.max(idx - 1, 0));
        } else if (event.key === 'Home') {
            event.preventDefault();
            setActiveIdx(0);
        } else if (event.key === 'End') {
            event.preventDefault();
            setActiveIdx(Math.max(results.length - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            execute(results[activeIdx]);
        }
    };

    // Group results for display (keep dynamic "Account" on top).
    const grouped = useMemo(() => {
        const order = ['Account', 'Brands', 'KCP', 'Notifications', 'System'];
        const map = new Map();
        results.forEach((r) => {
            if (!map.has(r.group)) map.set(r.group, []);
            map.get(r.group).push(r);
        });
        return order.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g) }));
    }, [results]);

    // Track flat index across groups so keyboard nav works globally.
    let flatIdx = 0;

    const activeAction = results[activeIdx];
    const activeDescendant = activeAction ? optionId(activeAction) : undefined;

    return (
        <Modal
            open={open}
            onCancel={close}
            footer={null}
            closable={false}
            width={isMobile ? '100vw' : 640}
            destroyOnHidden
            styles={{ body: { padding: 0 }, content: { padding: 0 } }}
            className='command-palette__modal'
            maskClosable
            centered={!isMobile}
            aria-label='Command palette'
            // Antd's default focus lands on the modal container; defer until after
            // the open transition so the input reliably owns the keyboard.
            afterOpenChange={(opened) => {
                if (opened) inputRef.current?.focus();
            }}
        >
            <div className='command-palette__input-row'>
                <SearchOutlined aria-hidden='true' className='command-palette__input-icon' />
                <Input
                    ref={inputRef}
                    variant='borderless'
                    placeholder='Search accounts, pages, actions…'
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    size='large'
                    name='command-palette-query'
                    autoComplete='off'
                    spellCheck={false}
                    aria-label='Search accounts, pages, and actions'
                    aria-autocomplete='list'
                    aria-controls={LISTBOX_ID}
                    aria-expanded={open}
                    aria-activedescendant={activeDescendant}
                    role='combobox'
                />
                {accountLoading ? <Spin size='small' aria-label='Loading results' /> : null}
            </div>
            <div
                id={LISTBOX_ID}
                role='listbox'
                aria-label='Search results'
                aria-live='polite'
                className='command-palette__results'
            >
                {results.length === 0 ? (
                    <div className='command-palette__empty'>
                        <Text type='secondary'>
                            {query
                                ? 'No matches — try a store name, page, or section keyword.'
                                : 'Start typing to search accounts and pages.'}
                        </Text>
                    </div>
                ) : (
                    grouped.map(({ group, items }) => (
                        <div key={group} className='command-palette__group' role='group' aria-label={group}>
                            <div className='command-palette__group-label' aria-hidden='true'>
                                <Text
                                    type='secondary'
                                    style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}
                                >
                                    {group}
                                </Text>
                            </div>
                            {items.map((action) => {
                                const idxForRow = flatIdx++;
                                return (
                                    <ResultRow
                                        key={action.id}
                                        action={action}
                                        active={idxForRow === activeIdx}
                                        onClick={() => execute(action)}
                                        onHover={() => setActiveIdx(idxForRow)}
                                    />
                                );
                            })}
                        </div>
                    ))
                )}
            </div>
            <div className='command-palette__hint'>
                <Text type='secondary' style={{ fontSize: 11 }}>
                    <kbd>↑</kbd> <kbd>↓</kbd> navigate · <kbd>↵</kbd> select · <kbd>Esc</kbd> close ·{' '}
                    <kbd>{'\u2318\u00A0K'}</kbd> to toggle
                </Text>
            </div>
        </Modal>
    );
};
